import { styled } from '@mui/material/styles';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import GrowDialogTransition from 'components/modal/GrowDialogTransition';
import { Dispatch, SetStateAction, useState } from 'react';
import { ReactComponent as PostDocument } from 'assets/icons/PostDocument.svg';

type SelectPostsCountModalProps = {
  open: boolean;
  onSubmit: (options: { isOnePostSelected: boolean }) => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

function SelectPostsCountModal({ open, onSubmit, setOpen }: SelectPostsCountModalProps) {
  const [isOnePostSelected, setOnePostSelected] = useState(true);

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      TransitionComponent={GrowDialogTransition}
      sx={{ '& .MuiPaper-root': { borderRadius: '16px' } }}
    >
      <CustomDialogTitle id="dialog-title" onClose={() => setOpen(false)}>
        <Title>How many posts do you want the AI to create?</Title>
      </CustomDialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" gap={4} p={3}>
          <Container
            tabIndex={0}
            sx={(theme) => ({
              borderColor: isOnePostSelected ? theme.palette.custom.tuna : 'transparent',
            })}
            onClick={() => setOnePostSelected(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setOnePostSelected(true);
              }
            }}
          >
            <PostDocument />
            <Typography
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 16,
              })}
            >
              Summarize Into a <b>Single Post</b>
            </Typography>
          </Container>
          <Container
            tabIndex={0}
            sx={(theme) => ({
              borderColor: isOnePostSelected ? 'transparent' : theme.palette.custom.tuna,
            })}
            onClick={() => setOnePostSelected(false)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                setOnePostSelected(false);
              }
            }}
          >
            <Box position="relative">
              <PostDocument />
              <PostDocument
                style={{
                  position: 'absolute',
                  top: 6,
                  left: 6,
                }}
              />
              <PostDocument
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                }}
              />
            </Box>
            <Typography
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 16,
              })}
            >
              Summarize Into <b>Multiple Posts</b>
            </Typography>
            <Typography variant="caption" mt={0.5} textAlign="center" fontSize={14}>
              Recommended for documents with multiple lessons learned
            </Typography>
          </Container>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => onSubmit({ isOnePostSelected })}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
  textAlign: 'center',
}));

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.whiteSmoke,
  padding: 8,
  borderRadius: 8,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  alignSelf: 'stretch',
  overflow: 'hidden',
  border: '2px solid transparent',
  cursor: 'pointer',
}));

export default SelectPostsCountModal;
